import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Menu {
  id: string;
  title: string;
  url: string;
  parent_id: string | null;
  order_index: number;
  icon: string;
  is_active: boolean;
  target: string;
}

export default function MenuManagement() {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    parent_id: '',
    order_index: 0,
    icon: '',
    is_active: true,
    target: '_self',
  });

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const { data, error } = await supabase
        .from('menus')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setMenus(data || []);
    } catch (error) {
      console.error('Menü yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const dataToSave = {
        ...formData,
        parent_id: formData.parent_id || null,
      };

      if (editingMenu) {
        const { error } = await supabase
          .from('menus')
          .update(dataToSave)
          .eq('id', editingMenu.id);

        if (error) throw error;
        alert('Menü güncellendi!');
      } else {
        const { error } = await supabase
          .from('menus')
          .insert([dataToSave]);

        if (error) throw error;
        alert('Menü eklendi!');
      }

      setShowModal(false);
      setEditingMenu(null);
      resetForm();
      fetchMenus();
    } catch (error) {
      console.error('Kayıt hatası:', error);
      alert('Bir hata oluştu!');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bu menüyü silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('menus')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Menü silindi!');
      fetchMenus();
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('Bir hata oluştu!');
    }
  };

  const handleEdit = (menu: Menu) => {
    setEditingMenu(menu);
    setFormData({
      title: menu.title,
      url: menu.url,
      parent_id: menu.parent_id || '',
      order_index: menu.order_index,
      icon: menu.icon || '',
      is_active: menu.is_active,
      target: menu.target || '_self',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      parent_id: '',
      order_index: 0,
      icon: '',
      is_active: true,
      target: '_self',
    });
  };

  const getParentMenus = () => {
    return menus.filter(m => !m.parent_id);
  };

  const getChildMenus = (parentId: string) => {
    return menus.filter(m => m.parent_id === parentId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Menü Yönetimi</h1>
          <p className="text-gray-600 mt-2">Site menülerini düzenleyin</p>
        </div>
        <button
          onClick={() => {
            setEditingMenu(null);
            resetForm();
            setShowModal(true);
          }}
          className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Yeni Menü Ekle
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          {getParentMenus().map((parent) => (
            <div key={parent.id} className="mb-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-2">
                <div className="flex items-center gap-4">
                  {parent.icon && <i className={`${parent.icon} text-xl text-teal-600`}></i>}
                  <div>
                    <h3 className="font-semibold text-gray-900">{parent.title}</h3>
                    <p className="text-sm text-gray-600">{parent.url}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    parent.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {parent.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                  <span className="text-sm text-gray-500">Sıra: {parent.order_index}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(parent)}
                    className="text-blue-600 hover:text-blue-800 p-2 cursor-pointer"
                  >
                    <i className="ri-edit-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(parent.id)}
                    className="text-red-600 hover:text-red-800 p-2 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              </div>

              {getChildMenus(parent.id).map((child) => (
                <div key={child.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg ml-12 mb-2">
                  <div className="flex items-center gap-4">
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                    {child.icon && <i className={`${child.icon} text-lg text-teal-600`}></i>}
                    <div>
                      <h4 className="font-medium text-gray-900">{child.title}</h4>
                      <p className="text-sm text-gray-600">{child.url}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      child.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {child.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                    <span className="text-sm text-gray-500">Sıra: {child.order_index}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(child)}
                      className="text-blue-600 hover:text-blue-800 p-2 cursor-pointer"
                    >
                      <i className="ri-edit-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(child.id)}
                      className="text-red-600 hover:text-red-800 p-2 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {menus.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Henüz menü eklenmemiş. Yeni menü eklemek için yukarıdaki butona tıklayın.
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingMenu ? 'Menü Düzenle' : 'Yeni Menü Ekle'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Menü Başlığı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Örn: Ana Sayfa"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="/"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Üst Menü
                  </label>
                  <select
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Ana Menü</option>
                    {getParentMenus().map((menu) => (
                      <option key={menu.id} value={menu.id}>{menu.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sıra
                  </label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İkon (Remix Icon)
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="ri-home-line"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hedef
                  </label>
                  <select
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="_self">Aynı Pencere</option>
                    <option value="_blank">Yeni Pencere</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Menüyü Aktif Et</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingMenu(null);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {editingMenu ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
